import * as React from 'react';

import { Loader } from '../../../Loader';
import { localeData } from '../../../../locales/data';
import { addModifiers, compareArrays } from '../../../../shared/helpers';
import { ErrorBoundary } from '../../../../containers/ErrorBoundary';
import { FormattedMessageHelper } from '../../../../shared/helpers/internationalization';

const Draggable = React.lazy(() => import('../../../../shared/helpers/react-beautiful-dnd/draggable'));
const Droppable = React.lazy(() => import('../../../../shared/helpers/react-beautiful-dnd/droppable'));
const DragDropContext = React.lazy(() => import('../../../../shared/helpers/react-beautiful-dnd/drag-drop-context'));

interface SettingsSectionState {
  items: any;
}

interface SettingsSectionProps {
  settingsStatus: boolean;
  sectionDetailsOrder: string[];
  handleReorder: (items: string[]) => void;
  resetOrder: () => void;
}

export class SettingsSection extends React.Component<SettingsSectionProps, SettingsSectionState> {
  constructor(props: SettingsSectionProps) {
    super(props);
    const sectionDetailsOrderCopy: any = this.props.sectionDetailsOrder;
    this.state = { items: [].concat(sectionDetailsOrderCopy) };
  }

  componentDidUpdate(prevProps: SettingsSectionProps) {
    if (!compareArrays(prevProps.sectionDetailsOrder, this.props.sectionDetailsOrder)) {
      const sectionDetailsOrderCopy: any = this.props.sectionDetailsOrder;
      this.setState({ items: [].concat(sectionDetailsOrderCopy) });
    }
  }

  getItemStyle = (_: any, draggableStyle: any) => ({
    userSelect: 'none',
    background: 'white',
    ...draggableStyle,
  });

  getListStyle = (_: any) => ({
    background: 'white',
  });

  onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const items = this.state.items;
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);

    this.setState({ items });
  };

  handleReset = () => {
    const sectionDetailsOrderCopy: any = this.props.sectionDetailsOrder;
    this.setState({ items: [].concat(sectionDetailsOrderCopy) });
    this.props.resetOrder();
  };

  handleApply = () => {
    this.props.handleReorder(this.state.items);
  };

  render() {
    const modifiers = this.props.settingsStatus === true ? ['active'] : [];

    return (
      <div className={addModifiers('alphas-details-sections__settings', modifiers)}>
        <h3 className="alphas-details-sections__settings-title">
          <FormattedMessageHelper
            tagName="span"
            id="alphasDetails.settings.title"
            defaultMessage={localeData.en['"alphasDetails.settings.title']}
          />
        </h3>
        <h4 className="alphas-details-sections__settings-subtitle">
          <FormattedMessageHelper
            tagName="span"
            id="alphasDetails.settings.subtitle"
            defaultMessage={localeData.en['alphasDetails.settings.subtitle']}
          />
        </h4>

        <div className="alphas-details-sections__settings-content">
          <ErrorBoundary showUI={true}>
            <React.Suspense fallback={<Loader active={true} inline="centered" size="massive" />}>
              <DragDropContext onDragEnd={this.onDragEnd}>
                <Droppable droppableId="droppable">
                  {(droppableProvided, droppableSnapshot) => (
                    <div
                      className="alphas-details-sections__settings-sortable"
                      ref={droppableProvided.innerRef}
                      style={this.getListStyle(droppableSnapshot.isDraggingOver)}
                    >
                      {this.state.items.map((item: string, index: number) => (
                        <Draggable key={`${item}-dragKey-${index}`} draggableId={`${item}-drag-${index}`} index={index}>
                          {(draggableProvided, draggableSnapshot) => (
                            <div
                              className={addModifiers('alphas-details-sections__settings-sortable-item', [item])}
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                              {...draggableProvided.dragHandleProps}
                              style={this.getItemStyle(draggableSnapshot.isDragging, draggableProvided.draggableProps.style)}
                            >
                              <FormattedMessageHelper
                                tagName="span"
                                id={`alphasDetails.settings.${item}`}
                                defaultMessage={localeData.en[`alphasDetails.settings.${item}`]}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {droppableProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </React.Suspense>
          </ErrorBoundary>

          <div className="alphas-details-sections__settings-actions">
            <button className="button button--secondary" onClick={this.handleReset}>
              <FormattedMessageHelper
                tagName="span"
                id="alphasDetails.settings.reset"
                defaultMessage={localeData.en['alphasDetails.settings.reset']}
              />
            </button>
            <button className="button button--primary" onClick={this.handleApply}>
              <FormattedMessageHelper
                tagName="span"
                id="alphasDetails.settings.apply"
                defaultMessage={localeData.en['alphasDetails.settings.apply']}
              />
            </button>
          </div>
        </div>
      </div>
    );
  }
}
